/**
 * Building map data hooks.
 *
 * `useBuildingMapLevels()` lists the level names discoverable for the
 * site (`GET .../building_map/levels`, derived by the backend from
 * the worker's per-level `building_map_revisions/*` keys) — used to
 * populate a level switcher before any specific level has been
 * fetched.
 *
 * `useBuildingMap(level)` is a TanStack Query for one level's
 * assembled building-map. Reads the real v1 `BuildingMapSnapshot`
 * (`GET /api/v1/sites/{site}/building_map?level=...`) plus the
 * door/lift registries (`GET .../doors`, `.../lifts`) and wraps them
 * in the legacy single-level `BuildingMap` shape `BuildingMapCanvas`
 * already renders — converting each `DoorRegistry`'s point+yaw+width
 * geometry into the legacy two-point line-segment shape, and each
 * `LiftRegistry`'s point+width/height into the legacy ref+width/depth
 * shape. The caller (not this hook) owns which level is "active" and
 * re-renders with a new `level` when the user switches floors — see
 * `OverviewScreen.tsx`'s level picker.
 *
 * Known gaps (map-parity follow-up, not fixed here):
 * - Places/nav-graph are not part of the v1 contract at all yet, so
 *   those overlay layers render empty regardless of backend.
 * - `BuildingMapCanvas`/`map-renderer.ts` still consume the legacy
 *   `BuildingMap` shape (from `ws-types.ts`) rather than v1 models
 *   natively — this hook is the adapter boundary by design (keeps
 *   the renderer's working, tested draw/hit-test math untouched); a
 *   full native-v1 renderer rewrite remains a separate, larger effort.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { buildingMapSnapshotQueryOptions } from '../query-options';
import { buildingMapKey, buildingMapLevelsKey } from '../scope';
import { fetchBuildingMapLevels, fetchDoorsRegistry, fetchLiftsRegistry } from '../v1-client';
import type { DoorRegistry, LiftRegistry, SiteId } from '../v1';
import type { BuildingMap } from '../ws-types';

type Level = BuildingMap['levels'][number];
type LegacyDoor = Level['doors'][number];
type LegacyLift = BuildingMap['lifts'][number];

function emptyGraph(name: string) {
  return { name, vertices: [], edges: [], params: [] };
}

/** Shared point+yaw+width -> legacy two-point line-segment
 *  conversion, used for both level-door registries and a lift
 *  cabin's own landing doors (`LiftRegistry.doors`) — same math,
 *  ported from `door_registry_geometry` in `rmf_bridge.py`. */
function pointYawWidthToLegacyDoor(
  name: string,
  v1_x: number,
  v1_y: number,
  v1_yaw: number,
  door_width_m: number,
  kind: 'single' | 'double',
): LegacyDoor {
  return {
    name,
    v1_x,
    v1_y,
    v2_x: v1_x + door_width_m * Math.cos(v1_yaw),
    v2_y: v1_y + door_width_m * Math.sin(v1_yaw),
    door_type: kind === 'double' ? 4 : 2,
    motion_range: Math.PI / 2,
    motion_direction: 1,
  };
}

function adaptDoorRegistry(d: DoorRegistry): LegacyDoor {
  return pointYawWidthToLegacyDoor(d.id.door, d.v1_x, d.v1_y, d.v1_yaw, d.door_width_m, d.kind);
}

function adaptLiftRegistry(l: LiftRegistry): LegacyLift {
  return {
    name: l.id.lift,
    levels: l.levels,
    // The cabin's own landing doors (`Lift.doors[]` upstream) — real
    // geometry, not a nearby-but-unrelated level `DoorRegistry` entry.
    // Named `${lift}-door-${i}` since a lift's own doors have no
    // separate identity of their own in the v1 contract (they're not
    // independently commandable — the lift command panel controls
    // the whole cabin, not one door).
    doors: (l.doors ?? []).map((d, i) =>
      pointYawWidthToLegacyDoor(`${l.id.lift}-door-${i}`, d.v1_x, d.v1_y, d.v1_yaw, d.door_width_m, d.kind),
    ),
    wall_graph: emptyGraph(`${l.id.lift}-walls`),
    ref_x: l.v1_x,
    ref_y: l.v1_y,
    ref_yaw: l.v1_yaw,
    width: l.door_width_m,
    depth: l.door_height_m,
  };
}

/**
 * The assembled map for one level. Its RAW part comes through the
 * snapshot query (`fetchQuery` on `buildingMapSnapshotKey`), never a
 * fetch of its own: the twin's floorplans observe that same query, so a
 * floor either view has loaded is not fetched again by the other, and the
 * two shapes never share a cache entry (`scope.ts`).
 *
 * `retry: false` on that inner fetch: the assembled query's own retry is
 * the only one on this path. `fetchQuery` otherwise inherits the client's
 * default (`retry: 1`, main.tsx), and each assembled attempt would run a
 * retried raw fetch -- four requests, and the registries twice over, for
 * one failing floor where there used to be two.
 */
async function fetchAssembledBuildingMap(
  client: QueryClient,
  site: SiteId,
  level: string,
): Promise<BuildingMap> {
  const [snapshot, doorRegistries, liftRegistries] = await Promise.all([
    client.fetchQuery({ ...buildingMapSnapshotQueryOptions(site, level), retry: false }),
    fetchDoorsRegistry(site),
    fetchLiftsRegistry(site),
  ]);
  const lifts = liftRegistries.map(adaptLiftRegistry);
  if (!snapshot) return { name: site, levels: [], lifts };

  const levelDoors = doorRegistries.filter((d) => d.level === snapshot.level).map(adaptDoorRegistry);
  const levelEntry: Level = {
    name: snapshot.level,
    elevation: 0,
    images: [
      {
        name: snapshot.level,
        x_offset: snapshot.affine.translation_x,
        y_offset: snapshot.affine.translation_y,
        yaw: snapshot.affine.rotation_rad,
        scale: snapshot.affine.scale,
        encoding: snapshot.image.encoding.replace('image/', ''),
        data: snapshot.image.data_base64,
      },
    ],
    places: [],
    doors: levelDoors,
    nav_graphs: [],
    wall_graph: emptyGraph(`${snapshot.level}-walls`),
  };
  return { name: site, levels: [levelEntry], lifts };
}

/** Level names discoverable for the active site — populate a level
 *  picker with this before any specific level's snapshot is fetched. */
export function useBuildingMapLevels() {
  const siteId = useSafeSiteId();
  return useQuery<string[]>({
    queryKey: buildingMapLevelsKey(siteId),
    queryFn: () => fetchBuildingMapLevels(siteId as SiteId),
    enabled: siteId.length > 0,
    staleTime: 60_000,
  });
}

/** `level: null` disables the query — the caller should wait for
 *  `useBuildingMapLevels()` to resolve and pick an initial level
 *  (e.g. the first one) before rendering the map. */
export function useBuildingMap(level: string | null) {
  const siteId = useSafeSiteId();
  return useQuery<BuildingMap>({
    queryKey: buildingMapKey(siteId, level ?? '__none__'),
    queryFn: ({ client }) => fetchAssembledBuildingMap(client, siteId as SiteId, level as string),
    enabled: siteId.length > 0 && level !== null,
    staleTime: 60_000,
  });
}
